import { Reducer } from 'redux';
import { Team } from './types';
import { TeamActions, TeamActionTypes } from './actionTypes';
import { ErrorResponse } from 'core/http/response';
import { ArchiveFilterValue } from 'components/new-ui/composed/archive-filter/ArchiveFilter';

export type TeamsPage = {
    pageNumber: number;
    pageSize: number;
    total: number;
};

export type TeamsState = {
    entities: Record<string, Team>;
    ids: string[];
    page: TeamsPage | null;
    loading: boolean;
    error: ErrorResponse | null;
    selectedId: string | null;
    archivationStatus: ArchiveFilterValue;
    invoicesCounts: Record<string, number>;
    manifestsCounts: Record<string, number>;
    jobsCounts: Record<string, number>;
};

const buildEntities = (list: Team[]) => {
    const entities: Record<string, Team> = {};
    // Noticed that when filtering sometimes the BE sends duplicates
    const seen = new Set<string>();
    const ids: string[] = [];
    for (const t of list) {
        entities[t.id] = t;
        if (!seen.has(t.id)) {
            seen.add(t.id);
            ids.push(t.id);
        }
    }
    return { entities, ids };
};

export const initialTeamsState: TeamsState = {
    entities: {},
    ids: [],
    page: null,
    loading: false,
    error: null,
    selectedId: null,
    archivationStatus: 'active',
    invoicesCounts: {},
    manifestsCounts: {},
    jobsCounts: {},
};

const teams: Reducer<TeamsState, TeamActionTypes> = (state = initialTeamsState, action) => {
    switch (action.type) {
        case TeamActions.LIST_TEAMS_SUCCESS: {
            const { list, pageNumber, total, pageSize } = action.payload;
            const { entities, ids } = buildEntities(list);
            const selectedStillExists = state.selectedId && !!entities[state.selectedId as string];
            return {
                ...state,
                loading: false,
                error: null,
                entities,
                ids,
                page: { pageNumber, pageSize, total },
                selectedId: selectedStillExists ? state.selectedId : null,
            };
        }

        case TeamActions.UPDATE_TEAM_SUCCESS:
        case TeamActions.ACTIVATE_TEAM_SUCCESS:
        case TeamActions.DEACTIVATE_TEAM_SUCCESS:
        case TeamActions.REMOVE_TEAM_MEMBER_SUCCESS:
        case TeamActions.ADD_TEAM_MEMBER_SUCCESS:
        case TeamActions.GET_TEAM_BY_ID_SUCCESS: {
            const team = action.payload as Team;
            const exists = !!state.entities[team.id];
            return {
                ...state,
                error: null,
                loading: false,
                entities: { ...state.entities, [team.id]: team },
                ids: exists ? state.ids : [...state.ids, team.id],
            };
        }

        case TeamActions.UPDATE_TEAM_REQUEST:
        case TeamActions.ACTIVATE_TEAM_REQUEST:
        case TeamActions.DEACTIVATE_TEAM_REQUEST:
        case TeamActions.REMOVE_TEAM_MEMBER_REQUEST:
        case TeamActions.ADD_TEAM_MEMBER_REQUEST:
        case TeamActions.GET_TEAM_BY_ID_REQUEST:
        case TeamActions.LIST_TEAMS_REQUEST: {
            return {
                ...state,
                loading: true,
            };
        }

        case TeamActions.UPDATE_TEAM_ERROR:
        case TeamActions.ACTIVATE_TEAM_ERROR:
        case TeamActions.DEACTIVATE_TEAM_ERROR:
        case TeamActions.REMOVE_TEAM_MEMBER_ERROR:
        case TeamActions.ADD_TEAM_MEMBER_ERROR:
        case TeamActions.GET_TEAM_BY_ID_ERROR:
        case TeamActions.LIST_TEAMS_ERROR: {
            return {
                ...state,
                loading: false,
                error: action.error,
            };
        }

        case TeamActions.SET_ARCHIVATION_STATUS_FILTER: {
            return {
                ...state,
                archivationStatus: action.payload,
            };
        }

        case TeamActions.GET_TEAM_INVOICES_COUNT_SUCCESS: {
            const { teamId, count } = action.payload;
            return {
                ...state,
                invoicesCounts: { ...state.invoicesCounts, [teamId]: count },
            };
        }

        case TeamActions.GET_TEAM_MANIFESTS_COUNT_SUCCESS: {
            const { teamId, count } = action.payload;
            return {
                ...state,
                manifestsCounts: { ...state.manifestsCounts, [teamId]: count },
            };
        }

        case TeamActions.GET_TEAM_JOBS_COUNT_SUCCESS: {
            const { teamId, count } = action.payload;
            return {
                ...state,
                jobsCounts: { ...state.jobsCounts, [teamId]: count },
            };
        }

        default: {
            return state;
        }
    }
};

export default teams;
